import React, { useEffect, useRef, useState } from 'react';

/**
 * Icon-triggered popover for secondary controls that would clutter a toolbar.
 *
 * Closes on outside click and on Escape, like ui/Modal and ui/InfoTip. Not
 * portalled: it is positioned relative to its trigger, which keeps it anchored when
 * the page scrolls — the viewers' content column is the scroll container.
 *
 * `active` puts a dot on the trigger. Always pass it when a setting inside can be
 * left on: a control that changes what the screen shows and then hides itself is how
 * someone ends up reading filtered numbers without knowing it.
 *
 * Props:
 *  - icon        : node for the trigger (defaults to a gear)
 *  - label       : accessible name of the trigger, also its tooltip
 *  - title?      : heading inside the panel
 *  - badge?      : node next to the title (e.g. getAdminBadge())
 *  - active?     : boolean — marks the trigger and keeps the panel discoverable
 *  - align?      : 'right' (default) | 'left'
 *  - width?      : Tailwind width utility for the panel
 *  - children    : panel content
 */
const GearIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const PopoverMenu = ({
  icon = GearIcon,
  label = 'Options',
  title = null,
  badge = null,
  active = false,
  align = 'right',
  width = 'w-80',
  className = '',
  children,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className={`relative inline-flex ${className}`}>
      <button
        type="button"
        aria-label={label}
        title={label}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((o) => !o)}
        className={`relative inline-flex items-center justify-center w-8 h-8 rounded-lg border transition-colors ${
          open || active
            ? 'border-purple-300 dark:border-purple-600 bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-300'
            : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/60'
        }`}
      >
        {icon}
        {active && (
          <span
            className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-purple-500 ring-2 ring-white dark:ring-gray-800"
            aria-hidden
          />
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={title || label}
          className={`absolute top-full mt-2 ${align === 'right' ? 'right-0' : 'left-0'} ${width} rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl z-50 p-3 text-left`}
        >
          {(title || badge) && (
            <div className="flex items-center gap-1.5 mb-2 text-xs font-semibold text-gray-900 dark:text-white">
              {badge}
              {title}
            </div>
          )}
          {children}
        </div>
      )}
    </div>
  );
};

export default PopoverMenu;
